import { useRef, useState } from "react";
import { Play } from "lucide-react";

/**
 * HoverVideo
 * - Shows a static poster/thumbnail by default
 * - Plays the video only while the cursor is hovering (or the card is touched)
 * - Falls back to a dark placeholder if no video/poster is provided yet
 */
export default function HoverVideo({ src, poster, alt = "Featured work", className = "", withAudio = false }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const hasVideo = !!src;

  const handleEnter = () => {
    if (!hasVideo) return;
    const v = ref.current;
    if (!v) return;
    if (withAudio) {
      v.muted = false;
      v.volume = 1;
    }
    setPlaying(true);
    const p = v.play();
    if (p && typeof p.catch === "function") {
      p.catch(() => {
        // Autoplay with sound may be blocked, fall back to muted playback
        if (withAudio && v) {
          v.muted = true;
          v.play().catch(() => {});
        }
      });
    }
  };

  const handleLeave = () => {
    const v = ref.current;
    if (!v) return;
    v.pause();
    try { v.currentTime = 0; } catch (_e) {}
    if (withAudio) v.muted = true;
    setPlaying(false);
  };

  return (
    <div
      className={`relative overflow-hidden rounded-2xl bg-[#0A0A0A] border border-[#222] hover:border-[#F4C75E] transition-colors duration-500 group cursor-pointer ${className}`}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onTouchStart={handleEnter}
      onTouchEnd={handleLeave}
    >
      {/* Poster / thumbnail */}
      {poster ? (
        <img
          src={poster}
          alt={alt}
          loading="lazy"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${playing ? "opacity-0" : "opacity-100"}`}
        />
      ) : !hasVideo ? (
        <div
          className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${playing ? "opacity-0" : "opacity-100"}`}
          style={{
            backgroundImage:
              "repeating-linear-gradient(45deg, rgba(244,199,94,0.06) 0px, rgba(244,199,94,0.06) 1px, transparent 1px, transparent 14px)",
          }}
        >
          <div className="flex flex-col items-center gap-2 text-[#666]">
            <div className="w-12 h-12 rounded-full border border-[#333] flex items-center justify-center">
              <Play className="w-4 h-4" />
            </div>
            <div className="text-[10px] uppercase tracking-[0.3em]">Awaiting upload</div>
          </div>
        </div>
      ) : null}

      {/* Video (mounted only if src provided). When no poster, video stays visible so its first frame acts as the thumbnail. */}
      {hasVideo && (
        <video
          ref={ref}
          src={src}
          muted
          loop
          playsInline
          preload="metadata"
          disablePictureInPicture
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
            poster ? (playing ? "opacity-100" : "opacity-0") : "opacity-100"
          }`}
        />
      )}

      {/* Hover play indicator */}
      {hasVideo && !playing && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-14 h-14 rounded-full bg-black/50 border border-white/30 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <Play className="w-5 h-5 fill-white text-white ml-0.5" />
          </div>
        </div>
      )}
    </div>
  );
}
